import { useEffect, useMemo, useState, useCallback } from "react";
import DashboardLayout from "../../common/DashboardLayout";
import PromoCodeServices from "../PromoCodeServices";
import { Tag, AlertCircle, CheckCircle } from "react-feather";
import "../../assets/css/custom.css";

/**
 * Make a discount code.
 *
 * The old way was the Influencers screen: six free-text fields, three unwritten
 * conventions (code prefix, owner name, and a UNIQUE dummy email per code), and
 * "Influencer already exists" when any of them collided. Here you pick who it is
 * for and how much off - the code name and the email are derived, and a percent
 * the owner already has is refused before you can submit it.
 */
const PromoCodes = () => {
  const [owners, setOwners] = useState([]);
  const [codes, setCodes] = useState([]);
  const [owner, setOwner] = useState("");
  const [percent, setPercent] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await PromoCodeServices.getPromoCodes();
      setOwners(r.owners || []);
      setCodes(r.codes || []);
      if (!owner && r.owners && r.owners.length) setOwner(r.owners[0].key);
    } catch (e) {
      setError(
        e?.response?.data?.error
        || (e?.response ? `Could not load codes (HTTP ${e.response.status}).` : null)
        || `This page failed before it could send the request: ${e?.message || e}`
      );
    }
  }, [owner]);

  useEffect(() => { load(); }, [load]);

  const selected = owners.find((o) => o.key === owner) || null;
  const pct = Number(percent);
  const pctValid = Number.isInteger(pct) && pct >= 1 && pct <= 100;

  // The whole point: say what will be made, and whether it already exists,
  // before anything is submitted.
  const preview = selected && pctValid ? `${selected.prefix}${pct}` : null;
  const clash = useMemo(() => {
    if (!selected || !pctValid) return null;
    if (selected.usedPercents.includes(pct)) {
      const existing = codes.find((c) => c.ownerKey === owner && c.rate === pct);
      return `${selected.label} already has a code at ${pct}%${existing ? `: ${existing.reemcode}` : ""}.`;
    }
    const taken = codes.find((c) => c.reemcode.toLowerCase() === String(preview).toLowerCase());
    if (taken) return `${taken.reemcode} already exists (${taken.name}, ${taken.rate}%).`;
    return null;
  }, [selected, pct, pctValid, codes, preview]);

  const canSubmit = !!selected && pctValid && !clash && !busy;

  const submit = async () => {
    setBusy(true); setError(""); setCreated(null);
    try {
      const r = await PromoCodeServices.createPromoCode(owner, pct);
      setCreated(r.code);
      setPercent("");
      await load();
    } catch (e) {
      setError(
        e?.response?.data?.error
        || (e?.response ? `Could not create the code (HTTP ${e.response.status}).` : null)
        || `This page failed before it could send the request: ${e?.message || e}`
      );
    } finally {
      setBusy(false);
    }
  };

  // Grouped by the owner the server reports, never by code prefix: AW100 /
  // AW2000 / AW2025 start with "AW" but belong to ANGELWATCH, not Vortek.
  const ownerCodes = selected
    ? codes.filter((c) => c.ownerKey === owner).sort((a, b) => a.rate - b.rate)
    : [];

  return (
    <DashboardLayout>
      <div className="dashboard-content" style={{ maxWidth: 820 }}>
        <h2 style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <Tag size={22} /> Promo Codes
        </h2>
        <p style={{ color: "#6b7280", marginBottom: 24 }}>
          Pick who the code is for and how much off. The code name is made for you.
        </p>

        <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 24, marginBottom: 28 }}>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div style={{ flex: "1 1 240px" }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Who is it for</label>
              <select
                className="form-select"
                value={owner}
                onChange={(e) => { setOwner(e.target.value); setCreated(null); }}
                style={{ width: "100%" }}
              >
                {owners.map((o) => (
                  <option key={o.key} value={o.key}>{o.label}</option>
                ))}
              </select>
            </div>

            <div style={{ flex: "0 1 170px" }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Discount</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  max="100"
                  placeholder="87"
                  value={percent}
                  onChange={(e) => { setPercent(e.target.value); setCreated(null); }}
                  style={{ width: 100 }}
                />
                <span style={{ fontSize: 18, color: "#6b7280" }}>% off</span>
              </div>
            </div>

            <button
              className="dashboard-addButton"
              onClick={submit}
              disabled={!canSubmit}
              style={{ opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? "pointer" : "not-allowed", height: 42 }}
            >
              {busy ? "Creating…" : "Create code"}
            </button>
          </div>

          {/* What will be made - shown before submitting, not after. */}
          {preview && !clash && (
            <p style={{ marginTop: 18, marginBottom: 0, fontSize: 16 }}>
              This will create <strong style={{ fontSize: 20, letterSpacing: 0.5 }}>{preview}</strong>
              {" "}— {pct}% off for {selected.label}.
            </p>
          )}

          {clash && (
            <p style={{ marginTop: 18, marginBottom: 0, display: "flex", alignItems: "center", gap: 8, color: "#b91c1c", fontWeight: 600 }}>
              <AlertCircle size={18} /> {clash}
            </p>
          )}

          {percent !== "" && !pctValid && (
            <p style={{ marginTop: 18, marginBottom: 0, color: "#b45309" }}>
              Enter a whole number between 1 and 100.
            </p>
          )}

          {created && (
            <p style={{ marginTop: 18, marginBottom: 0, display: "flex", alignItems: "center", gap: 8, color: "#166534", fontWeight: 600 }}>
              <CheckCircle size={18} /> Created <strong>{created.reemcode}</strong> — {created.rate}% off. It works immediately.
            </p>
          )}

          {error && (
            <p style={{ marginTop: 18, marginBottom: 0, color: "#b91c1c" }}>{error}</p>
          )}
        </div>

        {selected && (
          <>
            <h3 style={{ fontSize: 17, marginBottom: 10 }}>
              {selected.label} already has {ownerCodes.length} code{ownerCodes.length === 1 ? "" : "s"}
            </h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {ownerCodes.map((c) => (
                <span
                  key={c.reemcode}
                  title={`${c.rate}% off`}
                  style={{
                    border: "1px solid #d1d5db", borderRadius: 999, padding: "6px 14px",
                    fontSize: 14, background: "#f9fafb", fontVariantNumeric: "tabular-nums",
                  }}
                >
                  <strong>{c.reemcode}</strong> <span style={{ color: "#6b7280" }}>{c.rate}%</span>
                </span>
              ))}
              {ownerCodes.length === 0 && <span style={{ color: "#6b7280" }}>None yet.</span>}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PromoCodes;
